import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import API from '@/config/api';
import type { AiTemplate, AiPrompt } from '@/types/ai.types';
import {
  fetchAiTemplatesRequest,
  fetchAiTemplatesSuccess,
  fetchAiTemplatesFailure,
  fetchAiTemplateRequest,
  fetchAiTemplateSuccess,
  fetchAiTemplateFailure,
  createAiTemplateRequest,
  createAiTemplateSuccess,
  createAiTemplateFailure,
  updateAiTemplateRequest,
  updateAiTemplateSuccess,
  updateAiTemplateFailure,
  deleteAiTemplateRequest,
  deleteAiTemplateSuccess,
  deleteAiTemplateFailure,
  fetchPromptsRequest,
  fetchPromptsSuccess,
  fetchPromptsFailure,
  fetchPromptRequest,
  fetchPromptSuccess,
  fetchPromptFailure,
  createPromptRequest,
  createPromptSuccess,
  createPromptFailure,
  updatePromptRequest,
  updatePromptSuccess,
  updatePromptFailure,
  deletePromptRequest,
  deletePromptSuccess,
  deletePromptFailure,
} from './aiTemplateSlice';

function* handleFetchAiTemplates() {
  try {
    const r: { data: { data?: AiTemplate[] } | AiTemplate[] } = yield call(request.get, API.AI_TEMPLATES);
    // API returns data as direct array
    const templates = Array.isArray(r.data) ? r.data : (r.data.data || []);
    yield put(fetchAiTemplatesSuccess(templates));
  } catch (e: unknown) {
    console.error('handleFetchAiTemplates error:', e);
    yield put(fetchAiTemplatesFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchAiTemplate(action: PayloadAction<string>) {
  try {
    const r: { data: { data: AiTemplate } } = yield call(request.get, API.AI_TEMPLATE(action.payload));
    yield put(fetchAiTemplateSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleFetchAiTemplate error:', e);
    yield put(fetchAiTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleCreateAiTemplate(action: PayloadAction<Record<string, unknown>>) {
  try {
    const r: { data: { data: AiTemplate } } = yield call(request.post, API.AI_TEMPLATES, action.payload);
    yield put(createAiTemplateSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleCreateAiTemplate error:', e);
    yield put(createAiTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdateAiTemplate(action: PayloadAction<{ id: string; data: Record<string, unknown> }>) {
  try {
    const r: { data: { data: AiTemplate } } = yield call(request.patch, API.AI_TEMPLATE(action.payload.id), action.payload.data);
    yield put(updateAiTemplateSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleUpdateAiTemplate error:', e);
    yield put(updateAiTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleDeleteAiTemplate(action: PayloadAction<string>) {
  try {
    yield call(request.delete, API.AI_TEMPLATE(action.payload));
    yield put(deleteAiTemplateSuccess(action.payload));
  } catch (e: unknown) {
    console.error('handleDeleteAiTemplate error:', e);
    yield put(deleteAiTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchPrompts() {
  try {
    const r: { data: { data: { PromptList: AiPrompt[] } } } = yield call(request.get, API.TESTING_PROMPTS);
    // API returns data.data.PromptList
    yield put(fetchPromptsSuccess(r.data.data.PromptList || []));
  } catch (e: unknown) {
    console.error('handleFetchPrompts error:', e);
    yield put(fetchPromptsFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchPrompt(action: PayloadAction<string>) {
  try {
    const r: { data: { data: AiPrompt } } = yield call(request.get, API.TESTING_PROMPT(action.payload));
    yield put(fetchPromptSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleFetchPrompt error:', e);
    yield put(fetchPromptFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleCreatePrompt(action: PayloadAction<Record<string, unknown>>) {
  try {
    const r: { data: { data: AiPrompt } } = yield call(request.post, API.TESTING_PROMPTS, action.payload);
    yield put(createPromptSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleCreatePrompt error:', e);
    yield put(createPromptFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdatePrompt(action: PayloadAction<{ id: string; data: Record<string, unknown> }>) {
  try {
    const r: { data: { data: AiPrompt } } = yield call(request.patch, API.TESTING_PROMPT(action.payload.id), action.payload.data);
    yield put(updatePromptSuccess(r.data.data));
  } catch (e: unknown) {
    console.error('handleUpdatePrompt error:', e);
    yield put(updatePromptFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleDeletePrompt(action: PayloadAction<string>) {
  try {
    yield call(request.delete, API.TESTING_PROMPT(action.payload));
    yield put(deletePromptSuccess(action.payload));
  } catch (e: unknown) {
    console.error('handleDeletePrompt error:', e);
    yield put(deletePromptFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

export default function* aiTemplateSaga() {
  yield takeLatest(fetchAiTemplatesRequest.type, handleFetchAiTemplates);
  yield takeLatest(fetchAiTemplateRequest.type, handleFetchAiTemplate);
  yield takeLatest(createAiTemplateRequest.type, handleCreateAiTemplate);
  yield takeLatest(updateAiTemplateRequest.type, handleUpdateAiTemplate);
  yield takeLatest(deleteAiTemplateRequest.type, handleDeleteAiTemplate);
  yield takeLatest(fetchPromptsRequest.type, handleFetchPrompts);
  yield takeLatest(fetchPromptRequest.type, handleFetchPrompt);
  yield takeLatest(createPromptRequest.type, handleCreatePrompt);
  yield takeLatest(updatePromptRequest.type, handleUpdatePrompt);
  yield takeLatest(deletePromptRequest.type, handleDeletePrompt);
}
